// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Checkbox,
  ListItemText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Clear, FilterList } from '@mui/icons-material';

/** Fixed width: full MM/DD/YYYY + calendar icon (compact padding) */
const DATE_FIELD_WIDTH_PX = 148;

const datePickerTextFieldSx = {
  width: DATE_FIELD_WIDTH_PX,
  maxWidth: DATE_FIELD_WIDTH_PX,
  minWidth: 0,
  flexShrink: 0,
  '& .MuiInputBase-root': {
    pr: 0.25,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    px: 0.75,
    fontSize: '0.8125rem',
  },
  '& .MuiInputAdornment-root': {
    ml: 0,
    mr: 0,
  },
};

const DashboardFilters = ({ filters, filterOptions, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Language code to display name mapping
  const getLanguageDisplayName = (languageCode) => {
    const languageMap = {
      'en': 'English',
      'th': 'Thai', 
      'zh': 'Chinese',
      'ar': 'Arabic',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ru': 'Russian',
      'yue': 'Cantonese',
      'vi': 'Vietnamese',
      'tl': 'Tagalog',
      'id': 'Bahasa Indonesia',
      'other': 'Other'
    };
    return languageMap[languageCode] || languageCode;
  };

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Criteria list filtered by selected type (Matrix or Boolean)
  const criteriaByType = (filterOptions.criteria || []).filter(
    c => (c.criteria_type || 'matrix') === (localFilters.criteria_type || 'matrix')
  );
  const allCriteriaIdsOfType = criteriaByType.map(c => c.id);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };

  const handleCriteriaTypeChange = (newType) => {
    const criteria = filterOptions.criteria || [];
    const ofType = criteria.filter(c => (c.criteria_type || 'matrix') === newType);
    const idsOfType = ofType.map(c => c.id);
    const newFilters = {
      ...localFilters,
      criteria_type: newType,
      criteria_ids: idsOfType
    };
    setLocalFilters(newFilters);
  };

  const handleSelectAllTeams = () => {
    const allTeamIds = filterOptions.teams.map(t => t.id);
    const isAllSelected = localFilters.team_ids.length === allTeamIds.length;
    const newValue = isAllSelected ? [] : allTeamIds;
    handleFilterChange('team_ids', newValue);
  };

  const handleSelectAllCriteria = () => {
    const isAllSelected = localFilters.criteria_ids.length === allCriteriaIdsOfType.length;
    const newValue = isAllSelected ? [] : [...allCriteriaIdsOfType];
    handleFilterChange('criteria_ids', newValue);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleClearFilters = () => {
    const criteria = filterOptions.criteria || [];
    const matrixCriteria = criteria.filter(c => (c.criteria_type || 'matrix') === 'matrix');
    const defaultCriteriaIds = matrixCriteria.length > 0 ? matrixCriteria.map(c => c.id) : [];
    const allTeamIds = filterOptions.teams.map(t => t.id);
    const clearedFilters = {
      criteria_type: 'matrix',
      team_ids: allTeamIds,
      criteria_ids: defaultCriteriaIds,
      language: 'all',
      date_from: new Date(),
      date_to: new Date()
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.team_ids && localFilters.team_ids.length > 0 && localFilters.team_ids.length < filterOptions.teams.length) count++;
    if (localFilters.criteria_ids && localFilters.criteria_ids.length > 0 && localFilters.criteria_ids.length < criteriaByType.length) count++;
    if (localFilters.language && localFilters.language !== 'all') count++;
    if (localFilters.date_from) count++;
    if (localFilters.date_to) count++;
    return count;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">
            Filters
          </Typography>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={`${getActiveFiltersCount()} active`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            alignItems: 'center',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              // 4 dropdowns + 2 fixed date cols (was 5fr → 5th fr swallowed From, uneven gaps)
              md: `repeat(4, minmax(0, 1fr)) ${DATE_FIELD_WIDTH_PX}px ${DATE_FIELD_WIDTH_PX}px`,
            },
          }}
        >
          {/* Criteria Type (required) */}
          <Box sx={{ minWidth: 0 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Criteria Type</InputLabel>
              <Select
                value={localFilters.criteria_type || 'matrix'}
                onChange={(e) => handleCriteriaTypeChange(e.target.value)}
                label="Criteria Type"
              >
                <MenuItem value="matrix">Matrix</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Team Filter */}
          <Box sx={{ minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                multiple
                value={localFilters.team_ids || []}
                onChange={(e) => {
                  // Check if "select_all_teams" was clicked
                  if (e.target.value.includes("select_all_teams")) {
                    handleSelectAllTeams();
                  } else {
                    handleFilterChange('team_ids', e.target.value);
                  }
                }}
                label="Team"
                renderValue={(selected) => {
                  // When all teams are selected, show "All Teams"
                  if (selected.length === filterOptions.teams.length) return 'All Teams';
                  // When no teams are selected, show "NONE"
                  if (selected.length === 0) return 'NONE';
                  // When one team is selected, show the team name
                  if (selected.length === 1) {
                    const team = filterOptions.teams.find(t => t.id === selected[0]);
                    return team ? team.name : selected[0];
                  }
                  // When multiple teams are selected, show "Multiple Selected"
                  return 'Multiple Selected';
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                }}
              >
                <MenuItem 
                  value="select_all_teams"
                  sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}
                >
                  <Checkbox
                    checked={localFilters.team_ids && localFilters.team_ids.length === filterOptions.teams.length}
                    indeterminate={localFilters.team_ids && localFilters.team_ids.length > 0 && localFilters.team_ids.length < filterOptions.teams.length}
                  />
                  <ListItemText primary="Select All Teams" />
                </MenuItem>
                {filterOptions.teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    <Checkbox
                      checked={localFilters.team_ids && localFilters.team_ids.includes(team.id)}
                    />
                    <ListItemText primary={team.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* QA Criteria Filter (only criteria of selected type) */}
          <Box sx={{ minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel>QA Criteria</InputLabel>
              <Select
                multiple
                value={localFilters.criteria_ids || []}
                onChange={(e) => {
                  if (e.target.value.includes("select_all_criteria")) {
                    handleSelectAllCriteria();
                  } else {
                    handleFilterChange('criteria_ids', e.target.value);
                  }
                }}
                label="QA Criteria"
                renderValue={(selected) => {
                  if (selected.length === criteriaByType.length && criteriaByType.length > 0) return `All ${localFilters.criteria_type === 'boolean' ? 'Boolean' : 'Matrix'} Criteria`;
                  if (selected.length === 0) return 'NONE';
                  if (selected.length === 1) {
                    const c = criteriaByType.find(x => x.id === selected[0]);
                    return c ? c.name : selected[0];
                  }
                  return 'Multiple Selected';
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 250,
                    },
                  },
                }}
              >
                <MenuItem 
                  value="select_all_criteria"
                  sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}
                >
                  <Checkbox
                    checked={localFilters.criteria_ids && criteriaByType.length > 0 && localFilters.criteria_ids.length === criteriaByType.length}
                    indeterminate={localFilters.criteria_ids && localFilters.criteria_ids.length > 0 && localFilters.criteria_ids.length < criteriaByType.length}
                  />
                  <ListItemText primary={`Select All ${localFilters.criteria_type === 'boolean' ? 'Boolean' : 'Matrix'} Criteria`} />
                </MenuItem>
                {criteriaByType.map((criteria) => (
                  <MenuItem key={criteria.id} value={criteria.id}>
                    <Checkbox
                      checked={localFilters.criteria_ids && localFilters.criteria_ids.includes(criteria.id)}
                    />
                    <ListItemText primary={criteria.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Language Filter */}
          <Box sx={{ minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Language</InputLabel>
              <Select
                value={localFilters.language || 'all'}
                onChange={(e) => handleFilterChange('language', e.target.value)}
                label="Language"
              >
                <MenuItem value="all">
                  <em>All Languages</em>
                </MenuItem>
                {filterOptions.languages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {getLanguageDisplayName(language)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* From Date — fixed column width on md+ */}
          <Box
            sx={{
              width: { xs: '100%', sm: '100%', md: '100%' },
              maxWidth: { md: DATE_FIELD_WIDTH_PX },
              minWidth: 0,
            }}
          >
            <DatePicker
              label="From"
              value={localFilters.date_from}
              onChange={(date) => handleFilterChange('date_from', date)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: false,
                  inputProps: { title: 'From date' },
                  sx: datePickerTextFieldSx,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              width: { xs: '100%', sm: '100%', md: '100%' },
              maxWidth: { md: DATE_FIELD_WIDTH_PX },
              minWidth: 0,
            }}
          >
            <DatePicker
              label="To"
              value={localFilters.date_to}
              onChange={(date) => handleFilterChange('date_to', date)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: false,
                  inputProps: { title: 'To date' },
                  sx: datePickerTextFieldSx,
                },
              }}
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            startIcon={<FilterList />}
            size="small"
          >
            Apply Filters
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<Clear />}
            size="small"
          >
            Clear All
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardFilters;
